import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import type { Workstation } from "@redotech/redo-fulfillment-printing-common/workstation";
import { isWmsReturnsProcessingEnabled } from "@redotech/redo-fulfillment-wms-common/wms-constants";
import { TransferDistributionMode } from "@redotech/redo-fulfillment-wms-common/wms-constants-enums";
import { RedoAlert } from "@redotech/redo-web/arbiter-components/alert/redo-alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import {
  FormMultiSelectDropdown,
  FormSelectDropdown,
} from "@redotech/redo-web/select-dropdown";
import { Text } from "@redotech/redo-web/text";
import { InputSchema, groupInput, input } from "@redotech/ui/form";
import { memo, useState } from "react";
import { useParams } from "react-router-dom";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";
import { OutboundLabelForm } from "../outbound-labels";

export const wmsEnabledForm = groupInput({
  enabled: input<boolean>(),
  packingParcelsEnabled: input<boolean>(),
  binCoordinateMonitoringEnabled: input<boolean>(),
  binCoordinateMonitoringAlertThresholdPercent: input<number>(),
  sourceOfTruthEnabled: input<boolean>(),
  sourceOfTruthLocationIds: input<string[]>(),
  inferBinFromSku: input<boolean>(),
  processingAutoReceive: input<boolean>(),
  processingWmsEnabled: input<boolean>(),
  stationProcessingSessionMode: input<boolean>(),
  stationProcessingSessionWorkstationIds: input<string[]>(),
  receiveToBinEnabled: input<boolean>(),
  strictLicensePlateScan: input<boolean>(),
  strictLicensePlateScanBypassMerchantIds: input<string[]>(),
  inventoryDriftMonitoringLocationIds: input<string[]>(),
  transferDistributionMode: input<TransferDistributionMode>(),
  onboardingMode: input<"default" | "external">(),
});

export type WmsEnabledForm = InputSchema.Input<typeof wmsEnabledForm>;

export type WmsEnabledValue = InputSchema.Value<typeof wmsEnabledForm>;

export const wmsEnabledDefault: WmsEnabledValue = {
  enabled: false,
  packingParcelsEnabled: false,
  binCoordinateMonitoringEnabled: false,
  binCoordinateMonitoringAlertThresholdPercent: 80,
  sourceOfTruthEnabled: false,
  sourceOfTruthLocationIds: [],
  inferBinFromSku: false,
  processingAutoReceive: false,
  processingWmsEnabled: false,
  stationProcessingSessionMode: false,
  stationProcessingSessionWorkstationIds: [],
  receiveToBinEnabled: false,
  strictLicensePlateScan: false,
  strictLicensePlateScanBypassMerchantIds: [],
  inventoryDriftMonitoringLocationIds: [],
  transferDistributionMode: TransferDistributionMode.BIN_FIRST,
  onboardingMode: "external",
};

const TRANSFER_DISTRIBUTION_MODE_OPTIONS = Object.values(
  TransferDistributionMode,
);

const TRANSFER_DISTRIBUTION_MODE_LABELS: Record<
  TransferDistributionMode,
  string
> = {
  [TransferDistributionMode.BIN_FIRST]: "Bin first (default)",
  [TransferDistributionMode.ITEM_FIRST]: "Item first",
};

const ONBOARDING_MODE_OPTIONS: ("default" | "external")[] = [
  "default",
  "external",
];

const ONBOARDING_MODE_LABELS: Record<"default" | "external", string> = {
  default: "Default (Shopify inventory)",
  external: "External (WMS-only)",
};

const RESET_CONFIRMATION_PHRASE = "RESET WMS";

export const WmsEnabledCard = memo(function WmsEnabledCard({
  input,
  isOmsEngineer,
  workstations,
}: {
  input: OutboundLabelForm;
  isOmsEngineer: boolean;
  workstations: Workstation[];
}) {
  const { wmsEnabled } = input.inputs;
  const params = useParams();
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const teamId = params.teamId!;
  const client = useRequiredContext(RedoAdminRpcClientContext);

  const [showResetConfirmStep1, setShowResetConfirmStep1] = useState(false);
  const [showResetConfirmStep2, setShowResetConfirmStep2] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState("");
  const [resetInProgress, setResetInProgress] = useState(false);

  const crdbLocationsLoad = useLoad(async () => {
    const result = await client.getTeamCrdbLocations({ teamId });
    return result.locations;
  }, [client, teamId]);

  const locationOptions = crdbLocationsLoad.value ?? [];

  const returnsCenterLocationsLoad = useLoad(async () => {
    const result = await client.getTeamLocationsForAdmin({ teamId });
    return result.locations;
  }, [client, teamId]);

  const hasReturnsCenter =
    returnsCenterLocationsLoad.value?.some(
      (location) => location.isReturnsCenter,
    ) ?? false;
  const returnsCenterLocationsLoaded =
    returnsCenterLocationsLoad.value !== undefined;

  const managedMerchantsLoad = useLoad(async () => {
    const result = await client.getFulfillForTeams({
      fulfillmentServiceTeamId: teamId,
    });
    return result.teams;
  }, [client, teamId]);

  const managedMerchantOptions = managedMerchantsLoad.value ?? [];

  const workstationOptions = workstations.filter(
    (workstation): workstation is Workstation & { _id: string } =>
      workstation._id !== undefined,
  );

  const handleResetWmsSot = useHandler(async () => {
    setResetInProgress(true);
    try {
      await client.resetWmsSot({ teamId });
      setShowResetConfirmStep2(false);
      setResetConfirmText("");
    } finally {
      setResetInProgress(false);
    }
  });

  return (
    <>
      <Card title="WMS Settings" w="full">
        <Flex dir="column" gap="md">
          <Text fontSize="sm">
            Enable WMS (Warehouse Management System) functionality for pick
            sessions and warehouse operations.
          </Text>
          <RedoToggle
            label="Enable WMS"
            setValue={wmsEnabled.inputs.enabled.setValue}
            size="sm"
            value={wmsEnabled.inputs.enabled.value}
          />
          <RedoToggle
            label="Enable packing parcels"
            setValue={wmsEnabled.inputs.packingParcelsEnabled.setValue}
            size="sm"
            value={wmsEnabled.inputs.packingParcelsEnabled.value}
          />
          <RedoToggle
            disabled={!isOmsEngineer}
            label="WMS Source of Truth"
            setValue={wmsEnabled.inputs.sourceOfTruthEnabled.setValue}
            size="sm"
            value={wmsEnabled.inputs.sourceOfTruthEnabled.value}
          />
          {wmsEnabled.value.sourceOfTruthEnabled && (
            <>
              <RedoAlert
                color="warning"
                description="When enabled, WMS inventory is used as the source of truth for bin assignments during pick wave generation. Pick sessions will use incremental inventory-aware picking. Only enable this for warehouses with fully maintained WMS inventory."
                title="WMS Source of Truth is enabled"
              />
              <FormMultiSelectDropdown
                description="Select which locations use full WMS Source of Truth. If empty, all locations use SOT."
                display={(value) => {
                  if (!value.length) {
                    return "All locations (default)";
                  }
                  const names = value.map((id) => {
                    const loc = locationOptions.find((l) => l.id === id);
                    return loc ? loc.name : id;
                  });
                  return names.join(", ");
                }}
                input={wmsEnabled.inputs.sourceOfTruthLocationIds}
                label="Source of Truth locations"
                options={locationOptions.map((loc) => loc.id)}
              >
                {(id) => {
                  const loc = locationOptions.find((l) => l.id === id);
                  return loc ? `${loc.name} (${id})` : id;
                }}
              </FormMultiSelectDropdown>
            </>
          )}
          <RedoToggle
            label="Infer bin from SKU"
            setValue={wmsEnabled.inputs.inferBinFromSku.setValue}
            size="sm"
            value={wmsEnabled.inputs.inferBinFromSku.value}
          />
          <Flex dir="column" gap="sm">
            <Text fontSize="sm" fontWeight="medium" textColor="secondary">
              Returns processing
            </Text>
            <RedoToggle
              description="Automatically enabled when this account has a returns-center location."
              disabled={!returnsCenterLocationsLoaded || hasReturnsCenter}
              label="WMS returns processing"
              setValue={wmsEnabled.inputs.processingWmsEnabled.setValue}
              size="sm"
              value={isWmsReturnsProcessingEnabled(
                wmsEnabled.value,
                hasReturnsCenter,
              )}
            />
            <RedoToggle
              description="Automatically receive processed items into the receiving bin and sync inventory with Shopify."
              label="Processing auto-receive"
              setValue={wmsEnabled.inputs.processingAutoReceive.setValue}
              size="sm"
              value={wmsEnabled.value.processingAutoReceive}
            />
          </Flex>
          {wmsEnabled.value.processingAutoReceive && (
            <RedoAlert
              color="default"
              description="When enabled, completing a return processing session will auto-receive items into the receiving bin and sync inventory with Shopify."
              title="Processing auto-receive is enabled"
            />
          )}
          <RedoToggle
            label="Station processing session mode"
            setValue={wmsEnabled.inputs.stationProcessingSessionMode.setValue}
            size="sm"
            value={wmsEnabled.inputs.stationProcessingSessionMode.value}
          />
          {wmsEnabled.value.stationProcessingSessionMode && (
            <FormMultiSelectDropdown
              description="Limit station mode to specific workstations while piloting. If empty, every workstation runs it."
              display={(value) => {
                if (!value.length) {
                  return "All workstations (default)";
                }
                const names = value.map((id) => {
                  const workstation = workstationOptions.find(
                    (w) => w._id === id,
                  );
                  return workstation ? workstation.name : id;
                });
                return names.join(", ");
              }}
              input={wmsEnabled.inputs.stationProcessingSessionWorkstationIds}
              label="Station mode workstations"
              options={workstationOptions.map((w) => w._id)}
            >
              {(id) => {
                const workstation = workstationOptions.find(
                  (w) => w._id === id,
                );
                return workstation ? workstation.name : id;
              }}
            </FormMultiSelectDropdown>
          )}
          <RedoToggle
            label="Receive to Bin Enabled"
            setValue={wmsEnabled.inputs.receiveToBinEnabled.setValue}
            size="sm"
            value={wmsEnabled.inputs.receiveToBinEnabled.value}
          />
          <RedoToggle
            label="Strict license-plate scan"
            setValue={wmsEnabled.inputs.strictLicensePlateScan.setValue}
            size="sm"
            value={wmsEnabled.inputs.strictLicensePlateScan.value}
          />
          {wmsEnabled.value.strictLicensePlateScan && (
            <>
              <RedoAlert
                color="default"
                description="When enabled, receive to bin and pick session scans reject SKU scans and require scanning an inventory item license plate."
                title="Strict license-plate scan is enabled"
              />
              <FormMultiSelectDropdown
                description="Merchants in this list bypass strict mode — SKU scans from their products are accepted even though strict mode is on."
                display={(value) => {
                  if (!value.length) {
                    return "No merchants bypassed";
                  }
                  const names = value.map((id) => {
                    const merchant = managedMerchantOptions.find(
                      (m) => m._id === id,
                    );
                    return merchant ? merchant.name : id;
                  });
                  return names.join(", ");
                }}
                input={
                  wmsEnabled.inputs.strictLicensePlateScanBypassMerchantIds
                }
                label="Bypass merchants for strict license-plate scan"
                options={managedMerchantOptions.map((m) => m._id)}
              >
                {(id) => {
                  const merchant = managedMerchantOptions.find(
                    (m) => m._id === id,
                  );
                  return merchant ? merchant.name : id;
                }}
              </FormMultiSelectDropdown>
            </>
          )}
          <RedoToggle
            label="Enable bin coordinate monitoring"
            setValue={wmsEnabled.inputs.binCoordinateMonitoringEnabled.setValue}
            size="sm"
            value={wmsEnabled.inputs.binCoordinateMonitoringEnabled.value}
          />
          {wmsEnabled.value.binCoordinateMonitoringEnabled && (
            <RedoTextInput
              description="Alert when the percentage of line items with bin coordinates falls below this threshold"
              inputTypeHint="number"
              label="Alert threshold (%)"
              max={100}
              min={0}
              setValue={(value) =>
                wmsEnabled.inputs.binCoordinateMonitoringAlertThresholdPercent.setValue(
                  Number(value) || 0,
                )
              }
              value={String(
                wmsEnabled.value.binCoordinateMonitoringAlertThresholdPercent,
              )}
            />
          )}
          <FormMultiSelectDropdown
            description="Select warehouse locations to monitor for inventory drift, orphaned transit bins, and negative bins"
            display={(value) => {
              if (!value.length) {
                return "No locations selected";
              }
              const names = value.map((id) => {
                const loc = locationOptions.find((l) => l.id === id);
                return loc ? loc.name : id;
              });
              return names.join(", ");
            }}
            input={wmsEnabled.inputs.inventoryDriftMonitoringLocationIds}
            label="Inventory drift monitoring locations"
            options={locationOptions.map((loc) => loc.id)}
          >
            {(id) => {
              const loc = locationOptions.find((l) => l.id === id);
              return loc ? `${loc.name} (${id})` : id;
            }}
          </FormMultiSelectDropdown>
          <FormSelectDropdown
            input={wmsEnabled.inputs.transferDistributionMode}
            label="Transfer distribution mode"
            options={TRANSFER_DISTRIBUTION_MODE_OPTIONS}
          >
            {(option) => TRANSFER_DISTRIBUTION_MODE_LABELS[option]}
          </FormSelectDropdown>
          <FormSelectDropdown
            input={wmsEnabled.inputs.onboardingMode}
            label="Onboarding mode"
            options={ONBOARDING_MODE_OPTIONS}
          >
            {(option) => ONBOARDING_MODE_LABELS[option]}
          </FormSelectDropdown>
          <RedoButton
            hierarchy="secondary"
            onClick={() => setShowResetConfirmStep1(true)}
            size="sm"
            text="Reset WMS SOT onboarding data"
            theme="destructive"
          />
        </Flex>
      </Card>

      {/* Step 1: Are you sure? */}
      <RedoModal
        isOpen={showResetConfirmStep1}
        modalSize="sm"
        onModalCloseRequested={() => setShowResetConfirmStep1(false)}
        primaryButton={{
          text: "Continue",
          onClick: () => {
            setShowResetConfirmStep1(false);
            setShowResetConfirmStep2(true);
          },
          theme: "destructive",
        }}
        secondaryButton={{
          text: "Cancel",
          onClick: () => setShowResetConfirmStep1(false),
        }}
        theme="error"
        title="Reset WMS SOT onboarding data?"
      >
        <Flex dir="column" gap="md">
          <Text fontSize="sm">
            This will permanently delete ALL WMS Source of Truth data for this
            team, including:
          </Text>
          <Text fontSize="sm">
            - All inventory levels and ledger history
            {"\n"}- All inventory items and their events
            {"\n"}- All bin count sessions
            {"\n"}- All replenishment requests
            {"\n"}- All fulfillment group storage location mappings
            {"\n"}- All storage location transfers
            {"\n"}- All problem order demand reservations
          </Text>
          <Text fontSize="sm">
            Storage locations (bins, totes, etc.) are preserved.
          </Text>
          <RedoAlert
            color="error"
            description="This action cannot be undone. The team will need to re-run WMS onboarding to restore this data."
            title="This is a destructive action"
          />
        </Flex>
      </RedoModal>

      {/* Step 2: Type confirmation phrase */}
      <RedoModal
        isOpen={showResetConfirmStep2}
        modalSize="sm"
        onModalCloseRequested={() => {
          if (resetInProgress) {
            return;
          }
          setShowResetConfirmStep2(false);
          setResetConfirmText("");
        }}
        primaryButton={{
          text: resetInProgress ? "Resetting..." : "Reset all WMS SOT data",
          onClick: handleResetWmsSot,
          disabled:
            resetConfirmText !== RESET_CONFIRMATION_PHRASE || resetInProgress,
          theme: "destructive",
        }}
        secondaryButton={{
          text: "Cancel",
          onClick: () => {
            setShowResetConfirmStep2(false);
            setResetConfirmText("");
          },
        }}
        theme="error"
        title="Final confirmation"
      >
        <Flex dir="column" gap="md">
          <Text fontSize="sm">
            Type <strong>{RESET_CONFIRMATION_PHRASE}</strong> to confirm you
            want to delete all WMS SOT data for this team.
          </Text>
          <RedoTextInput
            label="Confirmation"
            placeholder={RESET_CONFIRMATION_PHRASE}
            setValue={setResetConfirmText}
            value={resetConfirmText}
          />
        </Flex>
      </RedoModal>
    </>
  );
});
